import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, ArrowDown } from 'lucide-react';

const LOG_COLORS = {
  info: 'text-blue-400',
  success: 'text-emerald-400',
  warning: 'text-amber-400',
  error: 'text-red-400',
  debug: 'text-muted-foreground',
  agent: 'text-purple-400',
  tool: 'text-cyan-400',
};

export default function LogViewer({ logs = [], autoScroll = true }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const scrollRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const filtered = useMemo(() => {
    return logs.filter((log) => {
      const matchesSearch = !search ||
        (log.content || '').toLowerCase().includes(search.toLowerCase());
      const matchesFilter = filter === 'all' || log.type === filter;
      return matchesSearch && matchesFilter;
    });
  }, [logs, search, filter]);

  useEffect(() => {
    if (autoScroll && isAtBottom && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [filtered, autoScroll, isAtBottom]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    setIsAtBottom(scrollHeight - scrollTop - clientHeight < 50);
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      setIsAtBottom(true);
    }
  };

  const types = useMemo(() => {
    const set = new Set(logs.map((l) => l.type).filter(Boolean));
    return ['all', ...Array.from(set)];
  }, [logs]);

  if (logs.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm">
        Aucun log pour le moment
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full relative">
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-3 border-b border-border">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher dans les logs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {types.map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`px-2 py-0.5 rounded text-xs transition-colors ${
                filter === t
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              {t === 'all' ? 'Tous' : t}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} log{filtered.length > 1 ? 's' : ''}
        </span>
      </div>

      {/* Logs */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto bg-background/50 font-mono text-xs p-3 space-y-0.5 max-h-[500px]"
      >
        {filtered.map((log, i) => (
          <div key={i} className="flex gap-2 py-0.5 hover:bg-accent/30 rounded px-1">
            <span className="text-muted-foreground/50 shrink-0 w-16 text-right">
              {log.timestamp
                ? new Date(log.timestamp).toLocaleTimeString('fr-FR')
                : ''}
            </span>
            <span className={`shrink-0 w-14 ${LOG_COLORS[log.type] || 'text-muted-foreground'}`}>
              [{log.type || 'log'}]
            </span>
            <span className="text-foreground/90 break-all whitespace-pre-wrap">
              {log.content || JSON.stringify(log)}
            </span>
          </div>
        ))}
      </div>

      {/* Scroll to bottom */}
      {!isAtBottom && (
        <div className="absolute bottom-4 right-4">
          <Button
            size="sm"
            variant="secondary"
            onClick={scrollToBottom}
            className="shadow-lg"
          >
            <ArrowDown className="w-3 h-3 mr-1" />
            Fin
          </Button>
        </div>
      )}
    </div>
  );
}
